/* global window */
import { createAction } from 'redux-actions';
import axios from 'axios';
import _ from 'lodash';

import * as Sentry from '@sentry/browser';

import { REALMS } from 'shared/realms';
import SharedConfig from 'shared/config';
import SimcInput from 'shared/simcinput';
import { isUnsupportedSpec, getSpecId, getCharacterSpecInfo } from 'shared/character';
import {
  iterationOptions,
  fightStyles,
  fightLengths,
} from 'shared/simcOptions';
import {
  transformInputToData,
  makeGearOptions,
} from 'shared/combinations';
import {
  FISHING_POLE,
  INVALID_WEAPON_TYPES,
} from 'shared/items';
import expansionOptions from 'shared/expansionOptions';
import ItemCache from 'shared/itemCache';

import SimcVersionSingleton from 'frontend/simcVersion';

import { dlog, derror, isBetaSite } from 'frontend/util';

import types from './actionTypes';
// eslint-disable-next-line import/no-cycle
import {
  selectors as simbotSelectors,
} from './reducers/simbot';
import {
  selectors as formSelectors,
  readyToSubmit,
} from './reducers/simbot/form';
import {
  selectors as characterSelectors,
} from './reducers/simbot/character';
import {
  selectors as userSelectors,
} from './reducers/user';
import {
  selectors as optimizeSelectors,
} from './reducers/simbot/optimize';
import { selectors as localeSelectors } from './reducers/locale';


const expansionOptionsActions = _.fromPairs(_.map(expansionOptions, opt => {
  return [
    opt.redux.actionFunc,
    createAction(types[opt.redux.actionType]),
  ];
}));

let nextGearsetId = 0;
let nextItemId = 0;
let nextTalentSetId = 0;

// abuse the fact that everything is in one file for armory fetch cancel
const CancelToken = axios.CancelToken;
let fetchArmoryCancelSource;

const formError = createAction(types.FORM_ERROR, (type, details) => ({ type, details }));

const fetchChar = async (dispatch, getState) => {
  const state = getState();
  const region = formSelectors.armoryRegion(state);
  const realm = formSelectors.armoryRealm(state);
  const name = formSelectors.armoryName(state);
  const currentChar = characterSelectors.data(state);
  if (region && realm && name) {
    dispatch({
      type: types.FETCH_CHAR_REQUEST,
      payload: {
        region,
        realm,
        name,
      },
    });
    fetchArmoryCancelSource = CancelToken.source();
    try {
      const response = await axios.get(
        `/wowapi/character/${region}/${realm}/${name}`,
        {
          cancelToken: fetchArmoryCancelSource.token,
        },
      );
      fetchArmoryCancelSource = null;
      let changed = false;
      if (currentChar && response.data) {
        changed = (
          (currentChar.name !== response.data.name)
          || (currentChar.realm !== response.data.realm)
          || (currentChar.lastModified !== response.data.lastModified)
        );
      }

      // update global ItemCache
      _.each(_.pickBy(response.data.items, _.isObject), ItemCache.update);

      dispatch({
        type: types.FETCH_CHAR_SUCCESS,
        payload: {
          ...response.data,
          changed,
        },
      });
    } catch (error) {
      if (axios.isCancel(error)) {
        dlog('armory fetch cancelled');
        dispatch({
          type: types.FETCH_CHAR_CANCELLED,
        });
      } else if (error.response && error.response.data) {
        const { status } = error.response;
        let reason = 'Armory communication failure';
        const errorCode = _.get(error, 'response.data.code');
        const errorReason = _.get(error, 'response.data.reason');

        if (errorCode && errorReason) {
          reason = errorReason;
        } else if (status === 403) {
          reason = 'No data - character has not logged in recently';
        } else if (status === 404) {
          reason = 'Character not found';
        } else if (status >= 500) {
          reason = 'Blizzard\'s Armory API is down or having issues';
        }
        dispatch({
          type: types.FETCH_CHAR_FAILURE,
          payload: {
            reason,
            status,
            data: error.response.data,
          },
          error: true,
        });
      } else {
        const reason = 'Armory communication failure';
        dispatch({
          type: types.FETCH_CHAR_FAILURE,
          payload: {
            reason,
          },
          error: true,
        });
      }
    }
  } else {
    // pretty hacky solution to the issue where there's no realm entered but there is a character name.
    // This just makes sure there isn't a permanently hanging loading state in these weird cases
    dispatch({
      type: types.FETCH_CHAR_CANCELLED,
    });
  }
};

const debouncedFetchChar = _.debounce(fetchChar, 1000);

const fetchSimcItems = (dispatch, getState) => {
  const state = getState();
  const text = formSelectors.text(state);
  const locale = localeSelectors.locale(state);

  dispatch({
    type: types.FETCH_SIMC_ITEMS_REQUEST,
    payload: {
      text,
    },
  });

  if (fetchArmoryCancelSource) {
    dlog('simc items request requesting cancel of armory fetch');
    fetchArmoryCancelSource.cancel('Cancelled by simc input');
  }

  axios.post('/api/simc-items', { text }, {
    headers: {
      'Content-Type': 'application/json',
      'x-simc-version': SimcVersionSingleton.get(),
    },
    params: { locale: locale.locale },
  }).then(response => {
    // side effect: update the global ItemCache
    _.each(response.data.items, ItemCache.update);
    _.each(response.data.bag, ItemCache.update);

    // dispatch
    dispatch({
      type: types.FETCH_SIMC_ITEMS_SUCCESS,
      payload: {
        items: response.data.items,
        bags: response.data.bag,
      },
    });
  }).catch(() => {
    dispatch({
      type: types.FETCH_SIMC_ITEMS_FAILURE,
      payload: {},
      error: true,
    });
  });
};

const debouncedFetchSimcItems = _.debounce(fetchSimcItems, 500);

const fetchGearInfo = (dispatch, getState) => {
  const state = getState();
  const character = simbotSelectors.character(state, { type: 'optimize' });
  const gear = optimizeSelectors.rawGear(state);
  const locale = localeSelectors.locale(state);

  const spec = getSpecId(character);

  dispatch({ type: types.FETCH_GEAR_INFO_REQUEST });

  const plvl = _.get(character, 'level');

  axios.post('/api/item-info', { gear }, {
    headers: {
      'Content-Type': 'application/json',
      'x-simc-version': SimcVersionSingleton.get(),
    },
    params: {
      locale: locale.locale,
      plvl,
      classId: _.get(character, 'class'),
      specId: spec,
      char: character?.name,
    },
    forceRetry: true,
  }).then(response => {
    // update global ItemCache
    _.each(response.data, (items) => _.each(items, ItemCache.update));

    const data = response.data;
    // hack to fix a temporary issue with a bad API response
    if (!data.off_hand) {
      data.off_hand = [];
    }

    dispatch({
      type: types.FETCH_GEAR_INFO_SUCCESS,
      payload: data,
    });
  }).catch(() => {
    dispatch({
      type: types.FETCH_GEAR_INFO_FAILURE,
      payload: {},
      error: true,
    });
  });
};

const debouncedFetchGearInfo = _.debounce(fetchGearInfo, 500);

const handleInputOptimizeText = (input, immediateFetch = false) => (dispatch, getState) => {
  let cleanInput = input;
  if (cleanInput) {
    // remove zero width characters
    cleanInput = cleanInput.replace(/[\u200B-\u200D\uFEFF]/g, '');
  }
  const data = transformInputToData(cleanInput);
  const gear = makeGearOptions(data);
  const talents = SimcInput.find(data, 'talents');
  const character = SimcInput.toCharacter(data);

  // update the text field
  dispatch({
    type: types.INPUT_OPTIMIZE_TEXT,
    payload: {
      input: cleanInput,
      gear,
      talents,
      character,
    },
  });

  if (cleanInput) {
    if (immediateFetch) {
      fetchGearInfo(dispatch, getState);
    } else {
      // debouncedFetchCombinations(dispatch, getState);
      debouncedFetchGearInfo(dispatch, getState);
    }
  }
};

const addOptimizeItem = (slot, item) => (dispatch, getState) => {
  // in order to make sure the new item is selected, we need to infer the new index.
  // to do that, we grab the gear from state and get the length of the slot
  const selectedIndex = optimizeSelectors.gear(getState())?.[slot]?.length;
  dispatch({
    type: types.ADD_OPTIMIZE_ITEM,
    payload: {
      slot,
      item,
      selectedIndex,
    },
  });
};


export default {
  changeRoute: createAction(types.CHANGE_ROUTE, () => ({})),

  showOverlay: createAction(types.SHOW_OVERLAY, overlay => overlay),
  clearOverlay: createAction(types.CLEAR_OVERLAY, () => {}),

  setTalentTreeOverlay: createAction(
    types.SET_TALENT_TREE_OVERLAY,
    (talents, specId, level) => ({ talents, specId, level }),
  ),
  clearTalentTreeOverlay: createAction(types.CLEAR_TALENT_TREE_OVERLAY, () => {}),

  addGearset: (gearset) => {
    if (!_.isEmpty(gearset)) {
      const ids = _.map(gearset.items, item => item.id);
      const maxId = _.max(ids);
      if (maxId > nextItemId) {
        nextItemId = maxId + 1;
      }

      return {
        type: types.ADD_GEARSET,
        payload: {
          gearsetId: nextGearsetId++, // eslint-disable-line no-plusplus
          gearset,
        },
      };
    }

    return {
      type: types.ADD_GEARSET,
      payload: {
        gearsetId: nextGearsetId++, // eslint-disable-line no-plusplus
        itemId: nextItemId++, // eslint-disable-line no-plusplus
      },
    };
  },
  removeGearset: createAction(types.REMOVE_GEARSET),
  duplicateGearset: gearsetId => ({
    type: types.DUPLICATE_GEARSET,
    payload: {
      gearsetId,
      newGearsetId: nextGearsetId++, // eslint-disable-line no-plusplus
    },
  }),
  resetGearsets: createAction(types.RESET_GEARSETS),


  inputGearsetName: createAction(types.INPUT_GEARSET_NAME, (gearsetId, name) => ({ gearsetId, name })),
  saveGearset: createAction(types.SAVE_GEARSET, (gearset) => ({ gearset })),
  deleteSavedGearset: createAction(types.DELETE_SAVED_GEARSET, (savedGearsetId) => ({ savedGearsetId })),
  chooseSavedGearset: createAction(types.CHOOSE_SAVED_GEARSET, (savedGearsetId) => ({ savedGearsetId })),

  addItem: (gearsetId) => ({
    type: types.ADD_ITEM,
    payload: {
      gearsetId,
      itemId: nextItemId++, // eslint-disable-line no-plusplus
    },
  }),
  removeItem: createAction(types.REMOVE_ITEM, (gearsetId, itemId) => ({ gearsetId, itemId })),

  inputItem: createAction(types.INPUT_ITEM, (gearsetId, itemId, item) => ({ gearsetId, itemId, item })),
  inputSlot: createAction(types.INPUT_ITEM_SLOT, (gearsetId, itemId, slot) => ({ gearsetId, itemId, slot })),
  inputItemLevel: createAction(types.INPUT_ITEM_LEVEL, (gearsetId, itemId, ilvl) => ({ gearsetId, itemId, ilvl })),
  inputGem: createAction(types.INPUT_ITEM_GEM, (gearsetId, itemId, slot, gem) => ({ gearsetId, itemId, slot, gem })),
  inputGemLevel: createAction(types.INPUT_ITEM_GEM_LEVEL, (gearsetId, itemId, slot, level) => ({
    gearsetId,
    itemId,
    slot,
    level,
  })),
  inputEnchant: createAction(
    types.INPUT_ITEM_ENCHANT,
    (gearsetId, itemId, enchant) => ({ gearsetId, itemId, enchant }),
  ),
  inputSecondary: createAction(
    types.INPUT_ITEM_SECONDARY,
    (gearsetId, itemId, bonusId) => ({ gearsetId, itemId, bonusId }),
  ),
  inputAffix: createAction(
    types.INPUT_ITEM_AFFIX,
    (gearsetId, itemId, bonusId) => ({ gearsetId, itemId, bonusId }),
  ),
  inputHand: createAction(
    types.INPUT_ITEM_HAND,
    (gearsetId, itemId, hand) => ({ gearsetId, itemId, hand }),
  ),
  inputLegendaryPower: createAction(
    types.INPUT_ITEM_LEGENDARY_POWER,
    (gearsetId, itemId, powerId) => ({ gearsetId, itemId, powerId }),
  ),
  inputLegendaryStats: createAction(
    types.INPUT_ITEM_LEGENDARY_STATS,
    (gearsetId, itemId, stats) => ({ gearsetId, itemId, stats }),
  ),
  inputCraftedStats: createAction(
    types.INPUT_ITEM_CRAFTED_STATS,
    (gearsetId, itemId, stats) => ({ gearsetId, itemId, stats }),
  ),
  inputEmbellishment: createAction(
    types.INPUT_ITEM_EMBELLISHMENT,
    (gearsetId, itemId, embellishmentId) => ({ gearsetId, itemId, embellishmentId }),
  ),

  // talents
  addTalentSet: (talents) => ({
    type: types.ADD_TALENT_SET,
    payload: {
      talents,
      id: nextTalentSetId++, // eslint-disable-line no-plusplus
    },
  }),
  duplicateTalentSet: (talentSetId) => ({
    type: types.DUPLICATE_TALENT_SET,
    payload: {
      talentSetId,
      id: nextTalentSetId++, // eslint-disable-line no-plusplus
    },
  }),
  removeTalentSet: createAction(types.REMOVE_TALENT_SET),

  chooseTalent: createAction(types.CHOOSE_TALENT, (id, row, col) => ({ id, row, col })),
  inputTalentSetName: createAction(types.INPUT_TALENT_SET_NAME, (id, name) => ({ id, name })),

  // job

  removeJob: createAction(types.REMOVE_JOB, jobId => jobId),

  // form
  clearForm: createAction(types.CLEAR_FORM, field => field),
  formError,
  toggleEmail: createAction(types.TOGGLE_EMAIL, () => undefined),
  toggleBrowserNotification: () => (dispatch, getState) => {
    const notificationPerm = _.get(window, 'Notification.permission');
    const enabled = formSelectors.sendBrowserNotification(getState());
    if (notificationPerm === 'default') {
      window.Notification.requestPermission(permission => {
        if (permission === 'granted') {
          dispatch({ type: types.TOGGLE_BROWSER_NOTIFICATION });
        }
      });
    } else if (notificationPerm === 'granted') {
      dispatch({ type: types.TOGGLE_BROWSER_NOTIFICATION });
    } else if (notificationPerm === 'denied' && enabled) {
      // allow turning off the setting if notifications got denied
      dispatch({ type: types.TOGGLE_BROWSER_NOTIFICATION });
    }
  },
  toggleOpenSimInNewTab: createAction(types.TOGGLE_OPEN_SIM_IN_NEW_TAB, _.noop),

  cancelJob: simId => dispatch => {
    dispatch({
      type: types.CANCEL_SIM,
      payload: simId,
    });

    axios.delete(`/api/job/cancel/${simId}`).catch(() => {});

    return Promise.resolve();
  },

  // simc options
  toggleAdvancedOptions: createAction(types.TOGGLE_ADVANCED_OPTIONS, () => undefined),
  selectSimcVersion: createAction(types.SELECT_SIMC_VERSION),
  selectIterations: createAction(types.SELECT_ITERATIONS),
  selectFightStyle: createAction(types.SELECT_FIGHT_STYLE),
  selectFightLength: createAction(types.SELECT_FIGHT_LENGTH),
  selectEnemyCount: createAction(types.SELECT_ENEMY_COUNT),
  selectEnemyType: createAction(types.SELECT_ENEMY_TYPE),
  selectPotion: createAction(types.SELECT_POTION),
  selectFood: createAction(types.SELECT_FOOD),
  selectFlask: createAction(types.SELECT_FLASK),
  selectAugmentation: createAction(types.SELECT_AUGMENTATION),
  setOptionsPreset: createAction(types.SET_OPTIONS_PRESET, preset => ({ preset })),
  toggleSmartHighPrecision: createAction(types.TOGGLE_SMART_HIGH_PRECISION, () => undefined),
  toggleSmartAggressive: createAction(types.TOGGLE_SMART_AGGRESSIVE, () => undefined),

  toggleBloodlust: createAction(types.TOGGLE_BLOODLUST, () => undefined),
  toggleArcaneIntellect: createAction(types.TOGGLE_ARCANE_INTELLECT, () => undefined),
  toggleFortitude: createAction(types.TOGGLE_FORTITUDE, () => undefined),
  toggleBattleShout: createAction(types.TOGGLE_BATTLE_SHOUT, () => undefined),
  toggleMysticTouch: createAction(types.TOGGLE_MYSTIC_TOUCH, () => undefined),
  toggleChaosBrand: createAction(types.TOGGLE_CHAOS_BRAND, () => undefined),
  toggleBleeding: createAction(types.TOGGLE_BLEEDING, () => undefined),
  toggleSkyfury: createAction(types.TOGGLE_SKYFURY, () => undefined),
  toggleMarkOfTheWild: createAction(types.TOGGLE_MARK_OF_THE_WILD, () => undefined),
  togglePowerInfusion: createAction(types.TOGGLE_POWER_INFUSION, () => undefined),
  toggleHuntersMark: createAction(types.TOGGLE_HUNTERS_MARK, _.noop),

  togglePtr: createAction(types.TOGGLE_PTR, () => undefined),
  toggleReportDetails: createAction(types.TOGGLE_REPORT_DETAILS, () => undefined),
  toggleShowAllOptions: createAction(types.TOGGLE_SHOW_ALL_OPTIONS, () => undefined),
  resetAdvancedOptions: createAction(types.RESET_ADVANCED_OPTIONS, () => undefined),

  toggleUseProfilesets: createAction(types.TOGGLE_USE_PROFILESETS, () => undefined),

  // custom APL
  toggleCustomApls: createAction(types.TOGGLE_CUSTOM_APLS, () => undefined),
  inputCustomApl: createAction(types.INPUT_CUSTOM_APL, (className, spec, apl) => ({ className, spec, apl })),
  clearCustomApl: createAction(types.CLEAR_CUSTOM_APL, (className, spec) => ({ className, spec })),

  // simc expert mode
  toggleSimcExpertMode: createAction(types.TOGGLE_SIMC_EXPERT_MODE, () => undefined),
  inputExpertHeader: createAction(types.INPUT_EXPERT_HEADER, input => input),
  inputExpertPreActors: createAction(types.INPUT_EXPERT_PRE_ACTORS, input => input),
  inputExpertRaidActors: createAction(types.INPUT_EXPERT_RAID_ACTORS, input => input),
  inputExpertPostActors: createAction(types.INPUT_EXPERT_POST_ACTORS, input => input),
  inputExpertFooter: createAction(types.INPUT_EXPERT_FOOTER, input => input),

  // report options
  toggleReportOptions: createAction(types.TOGGLE_REPORT_OPTIONS, () => undefined),
  toggleReportPawnName: createAction(types.TOGGLE_REPORT_PAWN_NAME, () => undefined),
  toggleReportPawnSpec: createAction(types.TOGGLE_REPORT_PAWN_SPEC, () => undefined),
  toggleReportPawnFight: createAction(types.TOGGLE_REPORT_PAWN_FIGHT, () => undefined),
  toggleReportPawnDate: createAction(types.TOGGLE_REPORT_PAWN_DATE, () => undefined),
  toggleReportPawnClass: createAction(types.TOGGLE_REPORT_PAWN_CLASS, () => undefined),

  chooseReportDroptimizerSort: createAction(types.CHOOSE_REPORT_DROPTIMIZER_SORT, sort => sort),
  toggleReportDroptimizerRelativeDps: createAction(types.TOGGLE_REPORT_DROPTIMIZER_RELATIVE_DPS, () => undefined),

  chooseReportOptimizeCompareAgainst: createAction(types.CHOOSE_REPORT_OPTIMIZE_COMPARE_AGAINST, ca => ca),

  toggleDetailedProgress: createAction(types.TOGGLE_DETAILED_PROGRESS, () => undefined),

  // user/login
  onLoginSuccess: createAction(types.ON_LOGIN_SUCCESS, user => user),
  onSubscriptionUpdate: createAction(types.ON_SUBSCRIPTION_UPDATE, user => user),
  onPreferencesUpdate: createAction(types.ON_PREFERENCES_UPDATE, user => user),

  // report
  loadGearReport: createAction(types.LOAD_GEAR_REPORT, data => {
    const gearsets = _.get(data, 'meta.rawFormData.gearsets');

    const gearsetIds = _.map(gearsets, gearset => gearset.id);
    const maxGearsetId = _.max(gearsetIds);
    if (maxGearsetId > nextGearsetId) {
      nextGearsetId = maxGearsetId + 1;
    }

    const itemIds = _.flatten(_.map(gearsets, gearset => _.map(gearset.items, 'id')));
    const maxItemId = _.max(itemIds);
    if (maxItemId > nextItemId) {
      nextItemId = maxItemId + 1;
    }

    return data;
  }),
  loadTalentsReport: createAction(types.LOAD_TALENTS_REPORT, data => {
    const ids = _.map(_.get(data, 'meta.rawFormData.talentSets'), talentSet => talentSet.id);
    const maxId = _.max(ids);
    if (maxId > nextTalentSetId) {
      nextTalentSetId = maxId + 1;
    }
    return data;
  }),
  loadOptimizeReport: createAction(types.LOAD_OPTIMIZE_REPORT, data => {
    return data.meta.rawFormData;
  }),
  loadDroptimizerReport: createAction(types.LOAD_DROPTIMIZER_REPORT, data => {
    return data.meta.rawFormData;
  }),

  toggleShowRelativeDifference: createAction(types.TOGGLE_SHOW_RELATIVE_DIFFERENCE, () => {}),
  togglePreferEquipped: createAction(types.TOGGLE_PREFER_EQUIPPED, () => {}),

  // flash message
  flashMessage: createAction(types.FLASH_MESSAGE, msg => msg),
  dismissFlash: createAction(types.DISMISS_FLASH, () => undefined),

  resetOptimize: (opts) => (dispatch, getState) => {
    const options = _.defaults(
      opts,
      {
        equipped: true,
        settings: true,
      },
    );
    const state = getState();
    if (options.settings) {
      const character = simbotSelectors.character(state, { type: 'optimize' });
      const specInfo = getCharacterSpecInfo(character);
      dispatch({
        type: types.RESET_OPTIMIZE,
        payload: {
          specId: specInfo.id,
        },
      });
    }

    if (options.equipped) {
      const input = optimizeSelectors.text(state);
      handleInputOptimizeText(input, true)(dispatch, getState);
    }
  },
  setOptimizeLoading: createAction(types.SET_OPTIMIZE_LOADING, () => {}),
  setOptimizeText: input => (dispatch, getState) => {
    dispatch({
      type: types.SET_OPTIMIZE_TEXT,
      payload: input,
    });

    if (input) {
      // debouncedFetchCombinations(dispatch, getState);
      debouncedFetchGearInfo(dispatch, getState);
    }
  },
  inputOptimizeText: handleInputOptimizeText,

  addOptimizeItem,
  addManyOptimizeItems: (slots) => (dispatch, getState) => {
    _.each(slots, (items, slot) => {
      _.each(items, item => {
        addOptimizeItem(slot, item)(dispatch, getState);
      });
    });
  },
  removeOptimizeItem: createAction(types.REMOVE_OPTIMIZE_ITEM, (slot, index) => ({ slot, index })),

  fetchCustomItems: options => async (dispatch) => {
    dispatch({
      type: types.FETCH_OPTIMIZE_CUSTOM_ITEMS_REQUEST,
    });

    try {
      const response = await axios.get('/api/custom-items', {
        params: options,
      });
      dispatch({
        type: types.FETCH_OPTIMIZE_CUSTOM_ITEMS_SUCCESS,
        payload: response.data,
      });
    } catch (error) {
      dispatch({
        type: types.FETCH_OPTIMIZE_CUSTOM_ITEMS_FAILURE,
        payload: error,
        error: true,
      });
    }
  },

  toggleCombinationItem: (slot, index) => (dispatch) => {
    dispatch({
      type: types.TOGGLE_COMBINATION_ITEM,
      payload: {
        slot,
        index,
      },
    });

    // fetchCombinations(dispatch, getState);
  },

  chooseOptimizeGem: createAction(
    types.CHOOSE_OPTIMIZE_GEM,
    (className, spec, value) => ({ className, spec, value }),
  ),
  chooseOptimizeCloakEnchant: createAction(
    types.CHOOSE_OPTIMIZE_CLOAK_ENCHANT,
    (className, spec, value) => ({ className, spec, value }),
  ),
  chooseOptimizeRingEnchant: createAction(
    types.CHOOSE_OPTIMIZE_RING_ENCHANT,
    (className, spec, value) => ({ className, spec, value }),
  ),
  chooseOptimizeNeckEnchant: createAction(
    types.CHOOSE_OPTIMIZE_NECK_ENCHANT,
    (className, spec, value) => ({ className, spec, value }),
  ),
  chooseOptimizeWeaponEnchant: createAction(
    types.CHOOSE_OPTIMIZE_WEAPON_ENCHANT,
    (className, spec, value) => ({ className, spec, value }),
  ),
  chooseOptimizeWeaponOffhandEnchant: createAction(
    types.CHOOSE_OPTIMIZE_WEAPON_OH_ENCHANT,
    (className, spec, value) => ({ className, spec, value }),
  ),
  chooseOptimizeReplaceExisting: createAction(types.CHOOSE_OPTIMIZE_REPLACE_EXISTING, value => value),
  toggleOptimizeUseMainStatGem: createAction(types.TOGGLE_OPTIMIZE_USE_MAIN_STAT_GEM, () => {}),
  toggleLock: createAction(types.TOGGLE_LOCK, (slot, idx) => ({ slot, idx })),

  toggleHideLowLevelItems: createAction(types.TOGGLE_OPTIMIZE_HIDE_LOW_LEVEL, () => {}),

  toggleEnhancementAdvancedControls: createAction(types.TOGGLE_OPTIMIZE_ENHANCEMENT_ADVANCED_CONTROLS, () => {}),
  toggleEnhancementReplace: createAction(types.TOGGLE_OPTIMIZE_ENHANCEMENT_REPLACE, () => {}),
  toggleOptimizeEnhancement: createAction(types.TOGGLE_OPTIMIZE_ENHANCEMENT, (specId, slot, enhancementId) => ({
    specId,
    slot,
    enhancementId,
  })),
  clearOptimizeEnhancements: createAction(types.CLEAR_OPTIMIZE_ENHANCEMENTS, (specId, slot) => ({ specId, slot })),
  setOptimizeEnhancementsDefault: createAction(types.SET_OPTIMIZE_ENHANCEMENTS_DEFAULT, (specId, slot) => ({
    specId,
    slot,
  })),
  setOptimizeEnhancementWrathionSocket: createAction(
    types.SET_OPTIMIZE_ENHANCEMENT_WRATHION_SOCKET,
    (specId, gemId) => ({ specId, gemId }),
  ),

  chooseOptimizeAddSocketNum: createAction(
    types.CHOOSE_OPTIMIZE_ADD_SOCKET_NUM,
    num => num,
  ),
  chooseOptimizeAddSocketGem: createAction(
    types.CHOOSE_OPTIMIZE_ADD_SOCKET_GEM,
    (specId, gemId) => ({ specId, gemId }),
  ),

  toggleOptimizeEnhancementsMainStatGem: createAction(
    types.TOGGLE_OPTIMIZE_ENHANCEMENTS_MAIN_STAT_GEM,
    specId => ({ specId }),
  ),
  toggleOptimizeEnhancementsAlgariColors: createAction(types.TOGGLE_OPTIMIZE_ENHANCEMENTS_ALGARI_COLORS, () => {}),
  setContaminantCount: createAction(types.CHOOSE_CONTAMINANT_COUNT, count => count),

  setUpgradeCurrencyConstraint: createAction(
    types.SET_UPGRADE_CURRENCY_CONSTRAINT, (currency, amount) => ({ currency, amount }),
  ),
  chooseItemSetRequirement: createAction(
    types.CHOOSE_ITEM_SET_REQUIREMENT,
    (itemSetId, num) => ({ itemSetId, num }),
  ),

  chooseAlternateWeapon: createAction(types.CHOOSE_ALTERNATE_WEAPON, (slot, index) => ({ slot, index })),

  chooseOptimizeTalentLoadout: createAction(types.CHOOSE_OPTIMIZE_TALENT_LOADOUT, (index) => ({ index })),
  addOptimizeTalentLoadout: createAction(
    types.ADD_OPTIMIZE_TALENT_LOADOUT,
    (index, talentString, name, generateCombos, exclude) => ({ index, talentString, name, generateCombos, exclude }),
  ),

  chooseCatalystCharges: createAction(types.CHOOSE_CATALYST_CHARGES, numCharges => numCharges),

  // droptimizer
  toggleDroptimizerInstance: createAction(types.TOGGLE_DROPTIMIZER_INSTANCE, id => id),
  chooseDroptimizerInstance: createAction(types.CHOOSE_DROPTIMIZER_INSTANCE, id => id),
  chooseDroptimizerEncounter: createAction(
    types.CHOOSE_DROPTIMIZER_ENCOUNTER,
    (instanceId, encounterId) => ({ instanceId, encounterId }),
  ),
  chooseDroptimizerDifficulty: createAction(
    types.CHOOSE_DROPTIMIZER_DIFFICULTY,
    (instanceId, difficultyId) => ({ instanceId, difficultyId }),
  ),
  chooseDroptimizerGroupBy: createAction(types.CHOOSE_DROPTIMIZER_GROUP_BY, groupBy => groupBy),
  chooseDroptimizerWarforgeLevel: createAction(
    types.CHOOSE_DROPTIMIZER_WARFORGE_LEVEL,
    (instanceId, difficulty, warforgeLevel) => ({ instanceId, difficulty, warforgeLevel }),
  ),
  chooseDroptimizerGem: createAction(types.CHOOSE_DROPTIMIZER_GEM, (instanceId, gemId) => ({ instanceId, gemId })),
  chooseDroptimizerLootSpec: createAction(
    types.CHOOSE_DROPTIMIZER_LOOT_SPEC,
    (classId, specId, lootSpecId) => ({ classId, specId, lootSpecId }),
  ),
  toggleDroptimizerShowPreviousTiers: createAction(types.TOGGLE_DROPTIMIZER_SHOW_PREVIOUS_TIERS, () => ({})),
  chooseDroptimizerCraftedStats: createAction(
    types.CHOOSE_DROPTIMIZER_CRAFTED_STAT,
    (specId, craftedStats) => ({ specId, craftedStats }),
  ),
  chooseDroptimizerCraftedGem: createAction(
    types.CHOOSE_DROPTIMIZER_CRAFTED_GEM,
    (specId, gemId) => ({ specId, gemId }),
  ),
  toggleDroptimizerOffSpecItems: createAction(
    types.TOGGLE_DROPTIMIZER_OFF_SPEC_ITEMS,
    () => {},
  ),
  toggleDroptimizerIncludeConversions: createAction(
    types.TOGGLE_DROPTIMIZER_INCLUDE_CONVERSIONS,
    () => {},
  ),
  chooseDroptimizerUpgradeLevel: createAction(
    types.CHOOSE_DROPTIMIZER_UPGRADE_LEVEL,
    (instanceId, difficulty, upgradeLevel) => ({ instanceId, difficulty, upgradeLevel }),
  ),
  setDroptimizerUpgradeEquipped: createAction(
    types.SET_DROPTIMIZER_UPGRADE_EQUIPPED,
    (instanceId, difficulty, upgradeEquipped) => ({ instanceId, difficulty, upgradeEquipped }),
  ),
  resetDroptimizer: createAction(types.RESET_DROPTIMIZER, _.noop),

  selectGreatVaultItems: () => (dispatch, getState) => {
    const state = getState();
    const gear = optimizeSelectors.gear(state);

    const vaultItemIndices = _.mapValues(
      gear,
      (items) => _.map(
        _.filter(
          _.map(items, (item, idx) => ({ ...item, idx })),
          item => _.includes(item.tags, 'weekly_reward'),
        ),
        'idx',
      ),
    );

    const vaultItems = [];
    _.each(vaultItemIndices, (indices, slot) => {
      _.each(indices, idx => {
        vaultItems.push({
          slot,
          index: idx,
        });
      });
    });

    dispatch({
      type: types.SELECT_OPTIMIZE_ITEMS,
      payload: {
        items: vaultItems,
      },
    });
  },

  fetchChar,
  inputReportName: createAction(types.INPUT_REPORT_NAME, name => name),
  inputForm: (field, value) => (dispatch, getState) => {
    let cleanInput = value;
    if (cleanInput) {
      // remove zero width characters
      cleanInput = cleanInput.replace(/[\u200B-\u200D\uFEFF]/g, '');
    }
    dispatch({
      type: types.INPUT_FORM,
      payload: {
        field,
        value: cleanInput,
      },
    });

    if (_.includes(['armoryName', 'armoryRealm'], field)) {
      debouncedFetchChar(dispatch, getState);
    }

    if (_.includes(['text'], field)) {
      debouncedFetchSimcItems(dispatch, getState);
    }
  },
  convertToProfilesets: input => dispatch => {
    try {
      const converted = SimcInput.convertCopyToProfilesets(input);
      dispatch({
        type: types.INPUT_FORM,
        payload: {
          field: 'advancedInput',
          value: converted,
        },
      });
    } catch (error) {
      dispatch(formError('profileset_convert_failure'));
      Sentry.captureException(error);
    }
  },

  submitForm: (formData, isLoggedIn) => (dispatch, getState) => new Promise((accept, reject) => {
    try {
      const { type, text, armory, advancedInput, optimize } = formData;
      const state = getState();

      const submitData = _.clone(formData);

      let allItems = [];

      if (type === 'advanced') {
        if (!advancedInput) {
          dispatch(formError('no_advanced_input'));
          return;
        }

        // check for banned commands
        const data = SimcInput.parse(advancedInput);

        const invalidCommands = SimcInput.getInvalidAdvancedCommands(data);

        if (invalidCommands.length > 0) {
          dispatch(formError('invalid_advanced_commands', { invalidCommands }));
          return;
        }

        if (SimcInput.find(data, 'armory') && SimcInput.getProfilesetNames(data).length > 0) {
          dispatch(formError('no_mix_armory_and_profilesets'));
          return;
        }

        // remove some fields that don't relate to advanced mode
        delete submitData.character;
        delete submitData.simcItems;
        delete submitData.optimize;
        delete submitData.bloodlust;
        delete submitData.arcaneIntellect;
        delete submitData.fortitude;
        delete submitData.battleShout;
        delete submitData.mysticTouch;
        delete submitData.chaosBrand;
        delete submitData.bleeding;
        delete submitData.skyfury;
        delete submitData.markOfTheWild;
        delete submitData.powerInfusion;
        delete submitData.huntersMark;
        delete submitData.augmentation;
        delete submitData.food;
        delete submitData.flask;
        delete submitData.potion;
        delete submitData.droptimizerItems;
      } else if (type === 'optimize') {
        // optimize error checking here

        const inputCharacter = SimcInput.toCharacter(optimize.data);
        submitData.character = inputCharacter;

        if (isUnsupportedSpec(inputCharacter) && !isBetaSite()) {
          dispatch(formError('unsupported_spec'));
          return;
        }

        const limit = userSelectors.iterationsLimit(state) || SharedConfig.iterationsLimit;

        if (optimize.numCombinations * submitData.iterations > limit) {
          dispatch(formError('over_iterations_limit'));
          return;
        }

        allItems = _.flatten(optimize.gear);

        delete submitData.advancedInput;
        delete submitData.armory;
        delete submitData.simcItems;
        delete submitData.droptimizer;
        delete submitData.droptimizerItems;

        if (submitData.character && submitData.character.items) {
          delete submitData.character.items;
        }
      } else {
        if (!readyToSubmit(text, armory.region, armory.realm, armory.name)) {
          dispatch(formError('not_ready'));
          return;
        }

        if (text) {
          // validate simc input
          const data = SimcInput.parse(text);
          const invalidCommands = SimcInput.getInvalidCommands(data);
          if (invalidCommands.length > 0) {
            dispatch(formError('invalid_commands', { invalidCommands }));
            return;
          }

          const actors = SimcInput.getActors(data);
          if (actors.length > 1) {
            dispatch(formError('too_many_cooks', { actors }));
            return;
          }

          const inputCharacter = SimcInput.toCharacter(data);

          if (isUnsupportedSpec(inputCharacter) && !isBetaSite()) {
            dispatch(formError('unsupported_spec'));
            return;
          }

          if (!SimcInput.isCompleteGearSet(data)) {
            dispatch(formError('missing_gear'));
            return;
          }

          submitData.character = inputCharacter;
        } else if (!submitData.character) {
          // validate character loaded from armory
          dispatch(formError('char_not_found'));
          return;
        } else if (isUnsupportedSpec(submitData.character) && !isBetaSite()) {
          dispatch(formError('unsupported_spec'));
          return;
        }

        // remove empty items from gearsets
        submitData.gearsets = submitData.gearsets.map(gearset => ({
          ...gearset,
          items: gearset.items.filter(item => item.item),
        }));
        // remove empty gearsets
        submitData.gearsets = submitData.gearsets.filter(gearset => gearset.items.length > 0);

        // item chooser error checks
        if (submitData.type === 'gear') {
          if (submitData.gearsets.length === 1 && submitData.gearsets[0].gearset === null) {
            dispatch(formError('no_items'));
            return;
          }
        }

        delete submitData.optimize;
        delete submitData.advancedInput;
        if (type !== 'droptimizer') {
          delete submitData.droptimizer;
          delete submitData.droptimizerItems;
        }
      }


      if (submitData.character && submitData.character.items) {
        allItems = _.filter(submitData.character.items, _.isObject);
      }

      // check for fishing poles
      const hasFishingPole = _.reduce(allItems, (result, item) => (
        result
        || (item.itemClass === FISHING_POLE.itemClass && item.itemSubClass === FISHING_POLE.itemSubClass)
      ), false);
      if (hasFishingPole) {
        dispatch(formError('fishing_pole_equipped'));
        return;
      }

      // check for invalid weapon types
      const hasInvalidWeapon = _.reduce(allItems, (result, item) => (
        result
        || Boolean(_.find(INVALID_WEAPON_TYPES, { itemClass: item.itemClass, itemSubClass: item.itemSubClass }))
      ), false);
      if (hasInvalidWeapon) {
        dispatch(formError('invalid_weapon_equipped'));
        return;
      }

      const { iterations, fightStyle, fightLength } = submitData;

      // check simc options
      const iterationOption = _.find(iterationOptions, { value: iterations });
      if (!iterationOption) {
        dispatch(formError('invalid_iterations'));
        return;
      }
      if (!iterationOption.public) {
        dispatch(formError('non_public_iterations'));
        return;
      }

      const fightStyleOption = _.find(fightStyles, { value: fightStyle });
      if (!fightStyleOption) {
        dispatch(formError('invalid_fight_style'));
        return;
      }
      if (!fightStyleOption.public) {
        dispatch(formError('non_public_fight_style'));
        return;
      }

      const fightLengthOption = _.find(fightLengths, { value: fightLength });
      if (!fightLengthOption) {
        dispatch(formError('invalid_fight_length'));
        return;
      }
      if (!fightLengthOption.public) {
        dispatch(formError('non_public_fight_length'));
        return;
      }

      dispatch({
        type: types.SUBMIT_SIM_REQUEST,
        payload: submitData,
      });

      axios.post('/sim', submitData, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
        // POSTs do not get retried by default. Testing out having this retry to reduce sim submit failures since it
        // appears that GCP has a lot of 502s as web server are scaled
        forceRetry: true,
        timeout: 45 * 1000,
      }).then((res) => {
        dispatch({
          type: types.SUBMIT_SIM_SUCCESS,
          payload: {
            data: res.data,
            // send this along to determine if we should save or not in redux
            isLoggedIn,
          },
        });
        accept(res.data);
      }).catch((err) => {
        if (err.response) {
          let errInfo;
          if (_.get(err, 'response.data.error')) {
            errInfo = err.response.data.error;
          } else {
            switch (err.response.status) {
              case 403:
                errInfo = 'forbidden';
                break;
              case 408:
                errInfo = 'request_timeout';
                break;
              case 429:
                errInfo = 'rate_limited';
                break;
              case 500:
                errInfo = 'internal_error';
                break;
              case 502:
              case 503:
              case 504:
                errInfo = 'site_unavailable';
                break;
              default:
                // if a status, log it
                // no status probably means network error
                if (err.response.status) {
                  Sentry.captureMessage(`Failed submit, unhandled response status: ${err.response.status}`);
                }
                errInfo = 'unknown_sim_submit_error';
                break;
            }
          }
          dispatch({
            type: types.SUBMIT_SIM_FAILURE,
            payload: errInfo,
            error: true,
          });
        } else {
          // no response at all, probably network failure
          dispatch({
            type: types.SUBMIT_SIM_FAILURE,
            payload: 'network_error',
            error: true,
          });
        }
        reject(err);
      });
    } catch (err) {
      Sentry.captureException(err);
      dispatch(formError('unhandled_submit_error'));
      derror(err);
    }
  }),

  fetchRealms: () => async (dispatch) => {
    dispatch({
      type: types.FETCH_REALMS_SUCCESS,
      payload: REALMS,
    });
  },

  fetchSims: () => dispatch => {
    dispatch({
      type: types.FETCH_SIMS_REQUEST,
    });
    axios.get('/api/sims', { withCredentials: true }).then((response) => {
      dispatch({
        type: types.FETCH_SIMS_SUCCESS,
        payload: response.data,
      });
    }).catch((err) => {
      dispatch({
        type: types.FETCH_SIMS_FAILURE,
        payload: err.data,
        error: true,
      });
    });
  },

  selectLocale: locale => dispatch => {
    dispatch({
      type: types.SELECT_LOCALE,
      payload: locale,
    });

    // background update of user profile if logegd in
  },

  fetchStatus: () => dispatch => {
    dispatch({
      type: types.FETCH_STATUS_REQUEST,
    });
    axios.get('/api/status', { withCredentials: true }).then(
      (response) => {
        dispatch({
          type: types.FETCH_STATUS_SUCCESS,
          payload: response.data,
        });
      },
      (err) => {
        dispatch({
          type: types.FETCH_STATUS_FAILURE,
          payload: err.data,
          error: true,
        });
      },
    );
  },

  ...expansionOptionsActions,
};
